import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';

/**
 * PrayerForm modal
 * Props:
 * - isOpen: boolean
 * - prayer: { id, userId, name, description, createdAt, category }
 * - user: firebase.User | null
 * - onClose: () => void
 * - onSaved?: (updatedPrayer) => void
 * - onDeleted?: (prayerId) => void
 */
export default function PrayerForm({ isOpen, prayer, user, onClose, onSaved, onDeleted }) {
  const canEdit = useMemo(() => !!(user && prayer && prayer.userId === user.uid), [user, prayer]);
  // Determine display name for prayer
  let displayName = prayer?.name || 'Anonymous';
  if (prayer?.isAnnonymous && prayer?.userId && user?.uid && prayer.userId !== user.uid) {
    displayName = 'Anonymous';
  }

  const [description, setDescription] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  // Lock page scroll when the modal is open
  useEffect(() => {
    if (!isOpen) return;

    const body = document.body;
    const html = document.documentElement;

    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    return () => {
      body.style.overflow = prevBodyOverflow || '';
      html.style.overflow = prevHtmlOverflow || '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!prayer) return;
    setDescription(prayer.description || '');
    setIsAnswered(prayer.isAnswered || false);
    setError('');
  }, [prayer]);

  if (!isOpen || !prayer) return null;

  const handleOverlayClick = (e) => {
    // Only close if clicking the backdrop, not inside the dialog
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      const updated = {
        description: description || '',
        isAnswered: isAnswered
      };
      await updateDoc(doc(db, 'prayers', prayer.id), updated);
      setSaving(false);
      const updatedPrayer = { ...prayer, ...updated };
      onSaved?.(updatedPrayer);
      onClose?.();
    } catch (err) {
      console.error('Failed to save prayer', err);
      setSaving(false);
      // Surface Firestore error code/message for easier troubleshooting
      const msg = (err && (err.message || err.code)) || 'Failed to save changes. Please try again.';
      setError(`Failed to save changes: ${msg}`);
    }
  };

  const handleDelete = async () => {
    if (!canEdit) return;
    if (!window.confirm('Are you sure you want to delete this prayer? This action cannot be undone.')) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'prayers', prayer.id));
      setDeleting(false);
      onDeleted?.(prayer.id);
      onClose?.();
    } catch (err) {
      console.error('Failed to delete prayer', err);
      setDeleting(false);
      const msg = (err && (err.message || err.code)) || 'Failed to delete prayer. Please try again.';
      setError(`Failed to delete prayer: ${msg}`);
    }
  };

  // Basic styles inlined to avoid external CSS changes
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: '#fff', width: '100%', maxWidth: 560, borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)', position: 'relative', padding: 20,
        }}
      >
        <button
          aria-label="Close"
          onClick={() => onClose?.()}
          style={{
            position: 'absolute', top: 10, right: 12, border: 'none', background: 'transparent',
            fontSize: 20, cursor: 'pointer', lineHeight: 1,
          }}
        >
          ×
        </button>

        {/* Header: Name, Date/Time, Category (read-only) */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 18 }}>
            {displayName}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {Array.isArray(prayer.category) ? prayer.category.join(', ') : (prayer.category || 'None')}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {(() => {
              const c = prayer.createdAt;
              let d = null;
              if (c?.seconds) d = new Date(c.seconds * 1000);
              else if (typeof c === 'number') d = new Date(c);
              else if (c?.toDate) { try { d = c.toDate(); } catch { d = null; } }
              else if (c instanceof Date) d = c;
              return d ? d.toLocaleString() : 'Unknown';
            })()}
          </div>
        </div>

        {error && (
          <div style={{ color: '#b91c1c', background: '#fee2e2', padding: 8, borderRadius: 6, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>Description</span>
            {canEdit ? (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={8}
                className="form-input"
                style={{ resize: 'vertical', minHeight: 160 }}
              />
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, background: '#fafafa' }}>
                {prayer.description || ''}
              </div>
            )}
          </label>

          {canEdit && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isAnswered}
                onChange={(e) => setIsAnswered(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 600 }}>Answered</span>
            </label>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          {canEdit && (
            <button
              onClick={handleDelete}
              className="button-secondary"
              disabled={deleting || saving}
              style={{ marginRight: 'auto' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          {canEdit && (
            <button
              onClick={handleSave}
              className="button-primary"
              disabled={saving || deleting || ((prayer.description || '') === description && (prayer.isAnswered || false) === isAnswered)}
              title={((prayer.description || '') === description && (prayer.isAnswered || false) === isAnswered) ? 'No changes to save' : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
